import React, { useEffect, useState, useCallback } from "react";
import { Inbox, CheckCircle2, XCircle, Loader2, Mail, Phone, MapPin, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const TABS = [
  { key: "submitted", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const STATUS_PILL = {
  submitted: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
  under_review: { cls: "bg-sky-100 text-sky-800", label: "Under review" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected" },
};

export default function AdminApplications() {
  const [status, setStatus] = useState("submitted");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/admin/applications", { params: { status, q: q || undefined, limit: 50 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load applications");
    }
  }, [status, q]);
  useEffect(() => { reload(); }, [reload]);

  const approve = async (item) => {
    setBusy(`ok-${item.id}`);
    try {
      await api.post(`/admin/applications/${item.id}/approve`, { note: noteDraft.trim() });
      toast.success(`Approved — ${item.name} is now a lawyer (pending KYC)`);
      setOpenId(null); setNoteDraft(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const reject = async (item) => {
    if (!noteDraft.trim()) { toast.error("A reason is required for rejection"); return; }
    setBusy(`no-${item.id}`);
    try {
      await api.post(`/admin/applications/${item.id}/reject`, { note: noteDraft.trim() });
      toast.success("Application rejected");
      setOpenId(null); setNoteDraft(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-applications-page">
      <div>
        <div className="flex items-center gap-2">
          <Inbox size={18} className="text-[#D4AF37]" />
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            Lawyer Applications
          </h1>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Review self-submitted lawyer applications. Approving creates a real lawyer profile (pending KYC verification).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="apps-toolbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`apps-tab-${t.key}`}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
              status === t.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
            }`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto">
          <Input
            data-testid="apps-search"
            placeholder="Search name / email / city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 rounded-xl w-64"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="apps-list">
        {data.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No applications in this bucket.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.items.map((item) => {
              const pill = STATUS_PILL[item.status] || { cls: "bg-slate-100 text-slate-700", label: item.status };
              const isOpen = openId === item.id;
              return (
                <li key={item.id} data-testid={`app-row-${item.id}`}>
                  <div className="p-4 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[260px]">
                      <p className="font-bold text-[#0A2540]" data-testid={`app-name-${item.id}`}>{item.name}</p>
                      <p className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="flex items-center gap-1"><Mail size={10} />{item.email}</span>
                        <span className="flex items-center gap-1"><Phone size={10} />{item.phone}</span>
                        <span className="flex items-center gap-1"><MapPin size={10} />{item.city}</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3">
                        <span><strong>{item.specialization}</strong> · {item.experience} yr</span>
                        <span className="flex items-center gap-1"><ScrollText size={10} /> BCI: {item.bci_enrolment_number}</span>
                        <span>· {item.created_at?.slice(0, 16).replace("T", " ")}</span>
                      </p>
                      {item.reviewer_note && (
                        <p className="mt-1 text-xs text-slate-600 italic">Reviewer note: {item.reviewer_note}</p>
                      )}
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pill.cls}`} data-testid={`app-status-${item.id}`}>
                      {pill.label}
                    </span>
                    {item.status === "submitted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`app-toggle-${item.id}`}
                        onClick={() => { setOpenId(isOpen ? null : item.id); setNoteDraft(""); }}
                        className="rounded-lg text-xs h-8"
                      >
                        {isOpen ? "Cancel" : "Decide"}
                      </Button>
                    )}
                  </div>
                  {isOpen && item.status === "submitted" && (
                    <div className="px-4 pb-4 -mt-1" data-testid={`app-decision-${item.id}`}>
                      {item.bio && <p className="text-xs text-slate-600 mb-2"><strong>Bio:</strong> {item.bio}</p>}
                      <Textarea
                        rows={3} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                        data-testid={`app-note-${item.id}`}
                        placeholder="Optional approval note · required for rejection"
                        className="rounded-xl text-sm"
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                          size="sm" data-testid={`app-approve-${item.id}`}
                          onClick={() => approve(item)} disabled={busy === `ok-${item.id}`}
                          className="rounded-lg text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {busy === `ok-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />} Approve
                        </Button>
                        <Button
                          size="sm" variant="destructive" data-testid={`app-reject-${item.id}`}
                          onClick={() => reject(item)} disabled={busy === `no-${item.id}`}
                          className="rounded-lg text-xs h-8"
                        >
                          {busy === `no-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} className="mr-1" />} Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-slate-400">
        {data.total} application{data.total === 1 ? "" : "s"} in this bucket.
      </p>
    </div>
  );
}
